// Макет приложения внутри тура: слои, поверх которых работает шаг.
//
// Это иллюстрация, а не настоящий интерфейс: ни стора, ни кликов здесь нет, и
// для скринридера сцена скрыта целиком — смысл шага живёт в его тексте рядом.
// Зато она собрана из тех же токенов, что и рабочие экраны, и подписывается
// теми же ключами: шаблоны, частые блоки, вкладки распознавания, роли и
// подписи доступа приезжают из настоящих модулей, а не выдуманы заново.
//
// Шаг сам по себе в стили не уезжает: компонент раскладывает его на состояния
// слоёв (data-trips, data-editor, data-fill, data-window). Селекторы читаются
// смыслом, а не номером, и добавленный шаг не заставляет переписывать CSS.
import { useEffect, useRef, type CSSProperties, type ReactElement } from 'react';
import { useT } from '../i18n';
import { KindIcon } from '../ui/library';
import { blockCountLabel, blockLabel } from '../templates';
import {
  MOCK_PRESETS,
  MOCK_TEMPLATES,
  MOCK_TRIPS,
  MOCK_VARIANTS,
  PICKED_TRIP,
  SAVINGS,
  WINNER,
  eur,
  stageView,
  sumOf,
  totalOf,
  type MockVariant,
} from './scenario';
import styles from './Stage.module.css';

/** Кастомные свойства в style: React их типом не знает, а CSS-анимации ждут. */
function vars(values: Record<string, string | number>): CSSProperties {
  return values as CSSProperties;
}

/**
 * Знак направления в списке поездок. Не самолёт из KindIcon: тот нарисован
 * плотно и на такой мелочи превращается в кляксу, а глобус остаётся глобусом
 * из трёх линий.
 */
function GlobeIcon(): ReactElement {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="12" cy="12" r="8.5" />
      <path d="M3.5 12h17" />
      <path d="M12 3.5c2.2 2.3 3.4 5.2 3.4 8.5S14.2 17.7 12 20.5c-2.2-2.8-3.4-5.2-3.4-8.5S9.8 5.8 12 3.5z" />
    </svg>
  );
}

/**
 * Список поездок — тот же, что на `/trips`: раздел «Мои поездки», строки с
 * действием справа и шаблоны под ними. Подписи берутся из общих ключей,
 * шаблоны — из настоящего каталога.
 */
function TripsLayer(): ReactElement {
  const t = useT();
  return (
    <div className={`${styles.layer} ${styles.trips}`}>
      <p className={styles.tripsHead}>{t('trips.section.mine')}</p>
      <ul className={styles.tripList}>
        {MOCK_TRIPS.map((trip, i) => (
          <li
            key={trip.id}
            className={`${styles.tripRow} ${trip.id === PICKED_TRIP ? styles.tripPicked : ''}`}
            style={vars({ '--i': i })}
          >
            <span className={styles.tripIcon}>
              <GlobeIcon />
            </span>
            <span className={styles.tripName}>{t(trip.nameKey)}</span>
            <span className={`mono ${styles.tripRoute}`}>
              {trip.route[0]} → {trip.route[1]}
            </span>
            <span className={styles.tripOpen}>{t('trips.openAction')}</span>
          </li>
        ))}
      </ul>

      <div className={styles.templates} style={vars({ '--i': MOCK_TRIPS.length })}>
        <span className={styles.templatesTitle}>{t('tpl.start')}</span>
        <span className={styles.chipRow}>
          {MOCK_TEMPLATES.map((tpl) => (
            <span key={tpl.id} className={styles.chip}>
              {t(tpl.nameKey)}
            </span>
          ))}
        </span>
      </div>
    </div>
  );
}

/** Колонка варианта: шапка, карточки, итоги внизу — как на настоящей доске. */
function VariantMock({ variant, scrolled }: { variant: MockVariant; scrolled: boolean }): ReactElement {
  const t = useT();
  const windowRef = useRef<HTMLDivElement>(null);
  const flights = sumOf(variant, 'flight');
  const lodging = sumOf(variant, 'lodging');
  const total = totalOf(variant);
  const wins = variant.id === WINNER;

  // Шаг итогов доезжает до низа ленты. Прокрутка настоящая, а не сдвиг на
  // заранее посчитанные пиксели: высота ленты зависит и от кегля из
  // персонализации, и от ширины окна, и подогнанное число разъехалось бы
  // с ней. scrollTop сам упирается в конец ленты.
  useEffect(() => {
    const el = windowRef.current;
    if (!el) return;
    el.scrollTop = scrolled ? el.scrollHeight : 0;
  }, [scrolled]);

  return (
    <div
      className={`${styles.variant} ${wins ? styles.variantWin : ''}`}
      style={vars({ '--variant-color': variant.color })}
    >
      <div className={styles.variantHead}>
        <span className={styles.dot} />
        <span className={styles.variantName}>{t(variant.nameKey)}</span>
        <span className={styles.variantNote}>{t(variant.noteKey)}</span>
      </div>

      {/* Шапка остаётся на месте, уезжает только лента с итогами: иначе на
          шаге сравнения две суммы висят без имён и сравнивать нечего. */}
      <div ref={windowRef} className={styles.variantWindow}>
        <div className={styles.variantScroll}>
          <ul className={styles.cards}>
            {variant.rows.map((row, i) => (
              <li key={`${variant.id}-${i}`} className={styles.card} style={vars({ '--i': i })}>
                <span
                  className={`${styles.cardIcon} ${
                    row.kind === 'flight' ? styles.flightIcon : styles.lodgingIcon
                  }`}
                >
                  <KindIcon kind={row.kind} size={11} />
                </span>
                <span className={styles.cardTitle}>
                  {row.route ? (
                    <span className={`mono ${styles.cardRoute}`}>
                      {row.route[0]} → {row.route[1]}
                    </span>
                  ) : (
                    t(row.labelKey ?? '')
                  )}
                </span>
                <span className={`mono ${styles.cardPrice}`}>{eur(row.price)}</span>
              </li>
            ))}
          </ul>

          {/* Разбивка по типам — теми же подписями, что и в настоящих итогах. */}
          <div className={styles.totals}>
            <div className={styles.totalLine}>
              <span>{t('kind.flight')}</span>
              <span className="mono">{eur(flights)}</span>
            </div>
            <div className={styles.totalLine}>
              <span>{t('kind.lodging')}</span>
              <span className="mono">{eur(lodging)}</span>
            </div>
            <div className={styles.totalSum}>
              <span className={styles.totalLabel}>{t('onboarding.mock.total')}</span>
              {/* Бейдж стоит в обеих колонках, но в проигравшей он невидим:
                  так строки итога остаются на одной высоте — а иначе на
                  телефоне, где бейдж уезжает под сумму, колонки разъезжаются
                  ровно там, где их и надо сравнивать. */}
              <span className={`${styles.win} ${wins ? '' : styles.winGhost}`}>
                {t('onboarding.mock.win', { sum: eur(SAVINGS) })}
              </span>
              <span className={`mono ${styles.totalValue}`}>{eur(total)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Экран редактирования поездки: полоса, библиотека слева, доска вариантов. */
function EditorLayer({ scrolled }: { scrolled: boolean }): ReactElement {
  const t = useT();
  return (
    <div className={`${styles.layer} ${styles.editor}`}>
      <div className={styles.bar}>
        <span className={styles.barBrand}>{t('app.brand')}</span>
        <span className={`${styles.barChip} ${styles.barChipWide}`} />
        <span className={styles.barChip} />
        <span className={styles.barSpacer} />
        <span className={styles.barChip} />
      </div>

      <div className={styles.cols}>
        {/* Библиотека макета — «Частые блоки» из настоящего каталога заготовок:
            именно с них начинается сбор варианта в пустой поездке. */}
        <aside className={styles.lib}>
          <p className={styles.libHead}>{t('library.title')}</p>
          <p className={styles.libSection}>{t('tpl.blocks')}</p>
          {MOCK_PRESETS.map((spec) => (
            <span key={spec.id} className={styles.libRow}>
              <KindIcon kind={spec.kind} size={10} />
              <span className={styles.libLabel}>{blockLabel(spec, t)}</span>
              <span className={styles.libCount}>{blockCountLabel(spec, t)}</span>
            </span>
          ))}
        </aside>

        <div className={styles.board}>
          <div className={styles.boardCols}>
            {MOCK_VARIANTS.map((variant) => (
              <VariantMock key={variant.id} variant={variant} scrolled={scrolled} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Общая рамка окна поверх редактора: у всех трёх одна шапка и одно движение. */
function WindowMock({ title, children }: { title: string; children: ReactElement }): ReactElement {
  return (
    <div className={styles.windowWrap}>
      <div className={styles.window}>
        <p className={styles.windowTitle}>{title}</p>
        {children}
      </div>
    </div>
  );
}

/** Шаг источников: вкладки распознавания, ссылка и разобранный черновик. */
function SourceWindow(): ReactElement {
  const t = useT();
  return (
    <WindowMock title={t('recog.title')}>
      <>
        <div className={styles.tabs}>
          {(['text', 'url', 'screenshot'] as const).map((tab) => (
            <span
              key={tab}
              className={`${styles.tab} ${tab === 'url' ? styles.tabActive : ''}`}
            >
              {t(`recog.tab.${tab}`)}
            </span>
          ))}
        </div>

        <p className={styles.fieldLabel}>{t('recog.url.label')}</p>
        <div className={styles.fieldRow}>
          <span className={`mono ${styles.field}`}>plan-flights.com/…/LCA-ATH</span>
          <span className={styles.fieldBtn}>{t('recog.run')}</span>
        </div>

        {/* Результат разбора: та же карточка, что потом ляжет в библиотеку. */}
        <p className={styles.windowNote}>{t('recog.review')}</p>
        <span className={`${styles.card} ${styles.draft}`}>
          <span className={`${styles.cardIcon} ${styles.flightIcon}`}>
            <KindIcon kind="flight" size={11} />
          </span>
          <span className={styles.cardTitle}>
            <span className={`mono ${styles.cardRoute}`}>LCA → ATH</span>
          </span>
          <span className={`mono ${styles.cardPrice}`}>{eur(39)}</span>
        </span>
      </>
    </WindowMock>
  );
}

/** Шаг соавторов: приглашение по почте и список тех, у кого есть доступ. */
function AccessWindow(): ReactElement {
  const t = useT();
  const trip = MOCK_TRIPS.find((x) => x.id === PICKED_TRIP);
  return (
    <WindowMock title={t('trips.access.title', { name: t(trip?.nameKey ?? '') })}>
      <>
        <p className={styles.fieldLabel}>{t('trips.access.invite')}</p>
        <div className={styles.fieldRow}>
          <span className={styles.field}>anna@example.com</span>
          <span className={styles.fieldBtn}>{t('trips.access.add')}</span>
        </div>

        <ul className={styles.people}>
          <li className={styles.person}>
            <span className={styles.personName}>you@example.com</span>
            <span className={styles.personYou}>{t('trips.access.you')}</span>
            <span className={styles.personRole}>{t('trips.access.role.owner')}</span>
          </li>
          <li className={`${styles.person} ${styles.personNew}`}>
            <span className={styles.personName}>anna@example.com</span>
            <span className={styles.personRole}>{t('trips.access.role.editor')}</span>
          </li>
        </ul>
      </>
    </WindowMock>
  );
}

/** Шаг публикации: ссылка на копию поездки только для чтения. */
function ShareWindow(): ReactElement {
  const t = useT();
  return (
    <WindowMock title={t('share.title')}>
      <>
        <div className={styles.windowHeadRow}>
          <p className={styles.fieldLabel}>{t('share.link')}</p>
          <span className={styles.readonly}>{t('share.readonly')}</span>
        </div>
        <div className={styles.fieldRow}>
          <span className={`mono ${styles.field}`}>plan-flights.com/s/7f3a2c</span>
          <span className={styles.fieldBtn}>{t('share.copy')}</span>
        </div>
        <p className={styles.windowNote}>{t('share.views', { count: 12 })}</p>
      </>
    </WindowMock>
  );
}

export interface StageProps {
  /** Номер шага, 1…7. Раскладывается на состояния слоёв в `stageView`. */
  step: number;
}

export function Stage({ step }: StageProps): ReactElement {
  const view = stageView(step);
  return (
    <div
      className={styles.stage}
      data-trips={view.trips}
      data-editor={view.editor}
      data-fill={view.fill}
      data-window={view.window ?? 'none'}
      aria-hidden="true"
    >
      <TripsLayer />
      <EditorLayer scrolled={view.scrolled} />
      {view.window === 'source' && <SourceWindow />}
      {view.window === 'access' && <AccessWindow />}
      {view.window === 'share' && <ShareWindow />}
    </div>
  );
}
